import React from "react";
import { Sparkles, Download, ArrowDown, Apple } from "lucide-react";
import { useLang } from "@/context/LangContext";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=eu.saubereko.tigik";

const T = {
  sk: {
    badge: "ŽIVÉ • NA GOOGLE PLAY",
    title: "Tigik je oficiálne na Google Play! 🎉",
    sub: "Stiahni si appku zadarmo. Plnohodnotná appka aj cez web.",
    cta: "Stiahnuť z Google Play",
    iosCta: "iOS app — už čoskoro",
    webLink: "iPhone? Nainštaluj cez web",
  },
  en: {
    badge: "LIVE • ON GOOGLE PLAY",
    title: "Tigik is now on Google Play! 🎉",
    sub: "Download free. Full-featured app also available via web.",
    cta: "Get it on Google Play",
    iosCta: "iOS app — coming soon",
    webLink: "iPhone? Install via web",
  },
  cs: {
    badge: "ŽIVĚ • NA GOOGLE PLAY",
    title: "Tigik je teď na Google Play! 🎉",
    sub: "Stáhni zdarma. Plnohodnotná aplikace i přes web.",
    cta: "Stáhnout z Google Play",
    iosCta: "iOS aplikace — již brzy",
    webLink: "iPhone? Nainstaluj přes web",
  },
  de: {
    badge: "LIVE • IM GOOGLE PLAY",
    title: "Tigik ist jetzt im Google Play! 🎉",
    sub: "Lade kostenlos. Vollwertige App auch über das Web.",
    cta: "Bei Google Play laden",
    iosCta: "iOS-App — bald verfügbar",
    webLink: "iPhone? Über Web installieren",
  },
  pl: {
    badge: "ŻYWE • W GOOGLE PLAY",
    title: "Tigik jest teraz w Google Play! 🎉",
    sub: "Pobierz za darmo. Pełnoprawna aplikacja również przez sieć.",
    cta: "Pobierz z Google Play",
    iosCta: "Aplikacja iOS — już wkrótce",
    webLink: "iPhone? Zainstaluj z sieci",
  },
  fr: {
    badge: "EN DIRECT • SUR GOOGLE PLAY",
    title: "Tigik est officiellement sur Google Play ! 🎉",
    sub: "Télécharge l'appli gratuitement. App complète aussi sur le web.",
    cta: "Télécharger depuis Google Play",
    iosCta: "App iOS — bientôt disponible",
    webLink: "iPhone ? Installe via le web",
  },
};

export default function GooglePlayLaunchBanner() {
  const { lang } = useLang();
  const t = T[lang] || T.sk;

  const scrollToInstall = (e) => {
    e.preventDefault();
    const el = document.getElementById("install-guide");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      data-testid="play-launch-banner"
      className="relative overflow-hidden border-y border-gold-500/40"
      style={{
        background:
          "linear-gradient(135deg, rgba(212,175,55,0.14) 0%, rgba(0,0,0,0.95) 50%, rgba(212,175,55,0.12) 100%)",
      }}
    >
      {/* subtle golden glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(40% 60% at 50% 50%, rgba(212,175,55,0.18), transparent 70%)",
        }}
      />

      {/* sparse confetti */}
      <div className="absolute inset-0 pointer-events-none opacity-50">
        <div className="absolute top-3 left-[8%] text-lg animate-bounce" style={{ animationDelay: "0s" }}>🎉</div>
        <div className="absolute top-4 right-[10%] text-lg animate-bounce" style={{ animationDelay: "0.6s" }}>⚒️</div>
      </div>

      <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-7 sm:py-8 text-center">
        {/* LIVE badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-gold-500 text-black text-[10px] font-mono font-black uppercase tracking-[0.2em] mb-3 shadow-lg shadow-gold-500/30">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-600"></span>
          </span>
          {t.badge}
          <Sparkles className="w-3 h-3" />
        </div>

        {/* Title — smaller */}
        <h2 className="font-display font-black text-xl sm:text-2xl lg:text-3xl text-white mb-2 leading-tight">
          {t.title}
        </h2>

        {/* Subtitle — compact */}
        <p className="text-sm text-zinc-300 max-w-xl mx-auto mb-5 leading-relaxed">
          {t.sub}
        </p>

        {/* Primary CTA — Google Play */}
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="play-store-cta"
          className="group inline-flex items-center gap-2.5 px-6 py-3 bg-white text-black font-display font-black text-sm uppercase tracking-wider hover:bg-gold-400 transition-all duration-200 shadow-xl shadow-gold-500/30 hover:scale-105"
        >
          <Download className="w-4 h-4" />
          <span>{t.cta}</span>
        </a>

        {/* Secondary line — iOS soon + web link */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-[11px] font-mono uppercase tracking-[0.15em] text-zinc-400">
          <span className="inline-flex items-center gap-1.5">
            <Apple className="w-3.5 h-3.5 text-zinc-500" />
            {t.iosCta}
          </span>
          <span className="hidden sm:inline text-zinc-700">·</span>
          <button
            type="button"
            onClick={scrollToInstall}
            data-testid="scroll-to-install-guide"
            className="inline-flex items-center gap-1 text-gold-400 hover:text-gold-300 underline underline-offset-4 transition-colors"
          >
            <ArrowDown className="w-3.5 h-3.5" />
            {t.webLink}
          </button>
        </div>
      </div>
    </section>
  );
}
